import { formatTime } from "@mp-commons/lib/utils/TimeUtils.js";
import { ParsedFormat } from "@mp-commons/modules/player/video/FormatAndSizePreference.js";
import { VideoContext } from "@mp-commons/modules/player/Player.js";
import { MegaridePlayerGUI } from "../../../../MegaridePlayer.js";
import { DetectedWebView, detectWebView } from "@ciaopeople/cp-shatsu/ClientDetection";
import { ControlButton } from "./ControlButton.js";
import { ControlSelectInline, selectItemForSettings, selectItemsForSettings } from "./ControlSelect.js";
import { IconName, getAudioIconName, getFullscreenIconName, getNextIconName, getPlayIconName, getPrevIconName } from "../../../../gui/IconSVG.js";
import { ControlProgress } from "./ControlProgress.js";
import { ReplaySubject } from "@ciaopeople/cp-shatsu/Observable";
import { PARTHENOPE_GLOBAL_CSS_PREFIX } from "../../ParthenopeGUIV2.js";
class ControlBarAudio {

    private mainHtml: HTMLElement;

    private audioButton: ControlButton;
    private sliderVolume: HTMLElement;
    private volume: HTMLElement;
    private wvDetect: DetectedWebView;
    private containerAbsolutePosition: DOMRect;
    private evtListeners: {
        volumechange: EventListener,
    }

    public disable(disable: boolean) {
        if (disable)
            this.mainHtml.classList.add("disabled")
        else
            this.mainHtml.classList.remove("disabled")
    }

    public active: ReplaySubject<boolean> = new ReplaySubject<boolean>(1, false);

    private updateContainerAbsolutePosition() {
        this.containerAbsolutePosition = this.gui.playerEl.getBoundingClientRect();
    }

    private get isDesktop(): boolean {
        return this.wvDetect.device?.other
    }

    constructor(private videoContext: VideoContext, private gui: MegaridePlayerGUI) {
        this.wvDetect = detectWebView();
        this.volume = null;
        this.sliderVolume = null;
        this.evtListeners = {
            volumechange: null
        }
        this.initHTML();
    }

    public setVideoContext(ctx: VideoContext) {
        const volumeChangeListener = (evt) => {
            this.updateAudio();
        }

        ctx.on("volumechange", volumeChangeListener)
        if (this.evtListeners?.volumechange) this.videoContext.off("volumechange", this.evtListeners.volumechange);

        this.videoContext = ctx;
        this.evtListeners.volumechange = volumeChangeListener;
        this.updateAudio();
    }


    private updateAudio() {
        this.videoContext.muted().then(muted => {
            this.audioButton.changeIcon(getAudioIconName(muted));
        })


        if (this.volume) {
            this.volume.style.width = `${this.videoContext.volume() * 100}%`;
        }
    }

    private attachVolumeListener(): void {

        this.audioButton.onClick((evt) => {
            this.videoContext.toggleAudio();
        });

        if (this.isDesktop) {
            this.active.next(false);

            const handleSeekVolume = (evt) => {
                const localX = (window.TouchEvent && evt instanceof TouchEvent ? evt.targetTouches[0]?.clientX || 0 : evt.clientX) - this.sliderVolume.offsetLeft - this.containerAbsolutePosition?.left || 0;
                const normalizedLocalX = localX < 0 ? 0 : (localX > this.sliderVolume.clientWidth ? this.sliderVolume.clientWidth : localX);
                const volume = Math.max(0, Math.min(normalizedLocalX * 100 / this.sliderVolume.clientWidth, 100)) / 100;
               
                this.videoContext.volume(volume);
            }

            const endSeekVolume = (evt) => {
                this.mainHtml.classList.remove("active");
                this.active.next(false);
                this.gui.window.removeEventListener("mousemove", mouseMoveListener);
                this.gui.window.removeEventListener("mouseup", mouseUpListener);

                 this.videoContext.muted().then(isMuted => {
                    const volume = this.videoContext.volume()
                    if(isMuted && volume > 0 || !isMuted && volume == 0) {
                        this.videoContext.toggleAudio()
                    }
                })
            }

            const mouseMoveListener = (evt) => {
                if (this.active.getCurrentValue()) {
                    evt.preventDefault();
                    handleSeekVolume(evt);
                }
            }

            const mouseUpListener = (evt) => {
                evt.preventDefault();
                endSeekVolume(evt)
                return false
            };

            const mouseDownListener = (evt) => {
                evt.preventDefault();
                this.updateContainerAbsolutePosition();
                this.active.next(true);
                this.mainHtml.classList.add("active");
                handleSeekVolume(evt);
                this.gui.window.addEventListener("mousemove", mouseMoveListener, { passive: false })
                this.gui.window.addEventListener("mouseup", mouseUpListener, { passive: false })

            }

            this.sliderVolume.addEventListener("mousedown", mouseDownListener, { passive: false })

        }

    }

    private initHTML() {
        const bClass = `${PARTHENOPE_GLOBAL_CSS_PREFIX}-lb`;

        const audioLevel = this.videoContext?.volume() || 0;

        this.audioButton = new ControlButton(getAudioIconName(audioLevel == 0));

        if (this.isDesktop) {
            this.sliderVolume = <div class={`${PARTHENOPE_GLOBAL_CSS_PREFIX}-bar__audio-level`}>
                <div class={`${bClass}`}>
                    <div class={`${bClass} ${bClass}--audio`}>
                        <div class={`${bClass}__inner`}>
                            <div class={`${bClass}__in ${bClass}__in--current`} style={`width: ${audioLevel * 100}%`} ref={(el: HTMLElement) => this.volume = el}>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        }

        this.mainHtml = <div class={`${PARTHENOPE_GLOBAL_CSS_PREFIX}-bar__audio`}>
            {this.audioButton.html}
            {this.sliderVolume}
        </div>

        this.attachVolumeListener()


    }

    public get html() {
        return this.mainHtml;
    }
}

export class ControlBar {

    private mainHtml: HTMLElement;

    private pie: HTMLElement;
    private playButton: ControlButton;
    private prevButton: ControlButton;
    private nextButton: ControlButton;
    private audioControls: ControlBarAudio;
    private settings: ControlSelectInline<ParsedFormat>;
    private subtitle: ControlSelectInline<string>;
    private fullscreenButton: ControlButton;
    private controlProgress: ControlProgress;
    private head: HTMLElement;
    private timeHead: HTMLElement;
    private durationHead: HTMLElement;
    private duration: number = 0;

    private videoContext: VideoContext;
    private wvDetect: DetectedWebView;


    private evtListeners: {
        play: EventListener,
        ended: EventListener,
        pause: EventListener,
        timeupdate: EventListener,
        loadedMetadata: EventListener
    }

    get active(): boolean {
        return this.audioControls?.active.getCurrentValue() || this.controlProgress.active.getCurrentValue();
    }

    public signalAdProgress(time: number, duration: number): void {
        this.controlProgress.signalAdProgress(time, duration)
    }

    get isMobile(): boolean {
        return this.wvDetect?.device?.phone || !!this.wvDetect.native_app;
    }

    constructor(
        private readonly gui: MegaridePlayerGUI,
        videoContext: VideoContext,
        private props: {
            static: boolean,
            isAdv: boolean,
            progressBar: {
                enableSeek: boolean,
                enableStoryboard: boolean
            },
            controls: {
                enable: boolean,
                enablePlaylistNavigation: boolean,
            }
        }) {

        this.wvDetect = detectWebView();
        this.pie = null;
        this.playButton = null;
        this.prevButton = null;
        this.nextButton = null;
        this.audioControls = null;
        this.videoContext = null;
        this.settings = null;
        this.subtitle = null;
        this.fullscreenButton = null;
        this.controlProgress = null;

        this.head = null;
        this.timeHead = null;
        this.durationHead = null;

        this.evtListeners = {
            play: null,
            pause: null,
            ended: null,
            timeupdate: null,
            loadedMetadata: null
        }

        if (videoContext) {
            this.setVideoContext(videoContext)
        }
        this.initHTML();
    }

    public toggleSettings(show: boolean): void {
        if (this.settings) {
            this.settings.toggle(show);
        }
    }

    public closeControls(): void {
        this.settings?.closeSelect();
        this.subtitle?.closeSelect();
    }


    get html(): HTMLElement {
        return this.mainHtml;
    }

    private initCounters() {
        this.duration = this.videoContext?.duration
        this.durationHead.innerText = this.duration ? formatTime(this.duration) : "--";
        this.setTime(this.duration ? formatTime(0) : "--")
        this.head.classList.remove("hidden");
    }

    private setTime(time: string): void {
        if (this.timeHead)
            this.timeHead.innerText = time;
    }

    public setVideoContext(ctx: VideoContext): void {

        if (this.head)
            this.head.classList.add("hidden");

        if (this.props.controls.enable) {

            const loadedMetadataListener = () => {
                if (this.settings) {
                    this.settings.updateSelectedItem(selectItemForSettings(this.videoContext.getCurrentFormat(), true))
                }
                this.videoContext.once("loadedmetadata", loadedMetadataListener)
            }


            ctx.metadataPromise.then(_ => {
                if (this.settings) {
                    this.settings.updateItems(
                        selectItemsForSettings(
                            ctx.getAvailableQualities(),
                            ctx.getCurrentFormat()
                        )
                    );

                    ctx.once("loadedmetadata", loadedMetadataListener)
                }
            })

            if (this.evtListeners?.loadedMetadata) this.videoContext.off("loadedmetadata", this.evtListeners.loadedMetadata);
            this.evtListeners.loadedMetadata = loadedMetadataListener;

            if (this.playButton) {
                const playListener = (evt) => {
                    this.playButton.changeIcon(getPlayIconName(false));
                };
                const pauseListener = (evt) => {
                    this.playButton.changeIcon(getPlayIconName(true));
                };

                this.playButton.changeIcon(getPlayIconName(ctx.isPaused));

                const endedListener = (evt) => {

                    if (!this.gui.forwardNavigationEnabled) {
                        this.playButton.changeIcon(IconName.restart)
                    }

                }

                ctx.on("ended", endedListener);
                ctx.on("play", playListener);
                ctx.on("pause", pauseListener);


                if (this.evtListeners?.play) this.videoContext.off("play", this.evtListeners.play);
                if (this.evtListeners?.pause) this.videoContext.off("pause", this.evtListeners.pause);
                if (this.evtListeners?.ended) this.videoContext.off("ended", this.evtListeners.ended);

                this.evtListeners.play = playListener;
                this.evtListeners.ended = endedListener;
                this.evtListeners.pause = pauseListener;
            }

            if (this.prevButton) {
                this.prevButton.updateStatus(this.gui.backNavigationEnabled)
            }

            if (this.nextButton) {
                this.nextButton.updateStatus(this.gui.forwardNavigationEnabled)
            }
        }


        if (!this.props.isAdv) {
            const timeupdateListener = (evt) => {
                const time = formatTime(this.videoContext.currentTime)
                this.setTime(time)
            };

            ctx.once("timeupdate", (evt) => {
                this.initCounters()
                ctx.on("timeupdate", timeupdateListener);
            });

            ctx.metadataPromise.then(_ => this.initCounters());

            if (this.evtListeners?.timeupdate) this.videoContext.off("timeupdate", this.evtListeners.timeupdate);

            this.evtListeners.timeupdate = timeupdateListener;

        }

        this.videoContext = ctx;
        this.controlProgress.setVideoContext(this.videoContext);
        this.audioControls?.setVideoContext(this.videoContext);

    }

    public fullscreenChanged(fullscreen: boolean): void {
        if (this.fullscreenButton) {
            this.fullscreenButton.changeIcon(getFullscreenIconName(fullscreen));
        }
    }

    public onChangeThumbPreview(listener: (evt) => void) {
        if (this.controlProgress) {
            this.controlProgress?.onChangeThumbPreview(listener);
        }
    }

    private initHTML(): void {

        const bClass = `${PARTHENOPE_GLOBAL_CSS_PREFIX}-bar`;

        this.controlProgress = new ControlProgress(this.videoContext, this.gui, { enableSeek: this.props.progressBar.enableSeek, enableStoryboard: this.props.progressBar.enableStoryboard, isAdv: this.props.isAdv });

        this.controlProgress.active.subscribe((active: boolean) => {
            this.audioControls?.disable(this.active);
        })

        if (this.props.controls.enable) {

            this.fullscreenButton = new ControlButton(getFullscreenIconName(this.gui.fullscreen));
            this.fullscreenButton.onClick((evt) => {
                this.gui.toggleFullscreen();
            });

            if (!this.isMobile) {
                this.playButton = new ControlButton(getPlayIconName(this.videoContext?.isPaused || true));

                this.playButton.onClick((evt) => {
                    if (this.gui.restartEnabled) {
                        this.gui.restart(true);
                    } else {
                        this.videoContext.togglePlay(true);
                    }
                });

                if (this.props.controls.enablePlaylistNavigation) {
                    this.prevButton = new ControlButton(getPrevIconName());
                    this.prevButton.onClick((evt) => {
                        this.gui.playPreviousVideo(true);
                    })
                    this.nextButton = new ControlButton(getNextIconName());
                    this.nextButton.onClick((evt) => {
                        this.gui.playNextVideo(true);
                    })
                }

                this.audioControls = new ControlBarAudio(this.videoContext, this.gui);

                this.settings = new ControlSelectInline<ParsedFormat>(IconName.settings, []);
                this.settings.onChange((switchTo: ParsedFormat) => {
                    this.videoContext.switchQuality(switchTo.label);
                })

                this.pie = <div class={`${bClass}__pie`}>
                    <div class={`${bClass}__pie-column ${bClass}__pie-column--left`}>
                        {this.prevButton?.html}
                        {this.playButton?.html}
                        {this.nextButton?.html}
                        {this.audioControls?.html}
                    </div>
                    <div class={`${bClass}__pie-column ${bClass}__pie-column--right`}>
                        {this.settings?.html}
                        {this.fullscreenButton?.html}
                    </div>
                </div>
            }




        }

        if (!this.props.isAdv) {
            this.head = <div class={`${bClass}__head hidden`}>
                <div class={`${bClass}__left`}>
                    <span class={`${bClass}__time ${bClass}__time--current`} ref={(el: HTMLElement) => this.timeHead = el}></span>
                    <span class={`${bClass}__time ${bClass}__time--full`} ref={(el: HTMLElement) => this.durationHead = el}></span>
                </div>
                <div class={`${bClass}__right`}>
                    {this.isMobile ? this.fullscreenButton?.html : null}
                </div>
            </div>
        }

        const barClass = `${this.props.static ? `${bClass}--s` : ''} ${!this.props.controls.enable || !this.pie ? `${bClass}--smaller` : ''}`;

        this.mainHtml = <div class={`${bClass} ${barClass}`}>
            <div class={`${bClass}__top`}>
                {this.head}
                {this.controlProgress.html}
            </div>
            {this.pie}
        </div>

    }



}
